import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import RemoteLayout from "./layout";
import { Conference } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { Button, ParticipantsReadyList } from "../../components/workshop";
import { defaultRoles } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute } from "../../util/general.util";

const TASK_ID = 9;

const Task9 = ({ summary = false, type, demoWorkshop = false }) => {
  const { t } = useTranslation();

  const [isStarted, setIsStarted] = useState(false);
  const [wordOptions, setWordOptions] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [totalSelections, setTotalSelections] = useState([]);
  const [readyUsers, setReadyUsers] = useState([]);
  const [finishedTask, setFinishedTask] = useState(false);

  useEffect(() => {
    try {
      if (wordOptions.length !== 0) return;

      const { attitudes } = t("mtParticipantsActivityNine", { returnObjects: true });

      setWordOptions(attitudes);
    } catch (error) {
      console.log(error);
    }
  }, [wordOptions]);

  const {
    socket,
    user: { id, role } = {},
    workshopDetails: { platform } = {},
    connectedUsers
  } = useContext(WorkshopContext);

  useEffect(() => {
    socket.emit("getTaskActivityStatus", TASK_ID, (myTaskStatus) => {
      if (!myTaskStatus) return;
      setIsStarted(myTaskStatus?.ready);
    });

    socket?.on("taskActivityStatus", (taskActivityStatus) => {
      const myTaskStatus = taskActivityStatus[TASK_ID];
      if (!myTaskStatus) return;
      if (myTaskStatus?.ready) setIsStarted(true);
    });

    socket.emit("task9ConfirmedUsers");

    socket.on("task9ConfirmedUsers", (confirmedUsers) => {
      setReadyUsers(confirmedUsers || []);
    });

    socket.emit("task9User", id);

    socket.on("task9User", (confirmed) => {
      setConfirmed(confirmed);
    });
  }, [id]);

  const selectWord = (index) => {
    if (totalSelections.includes(index)) {
      setTotalSelections((prev) => prev.filter((item) => item !== index));
    } else {
      if (totalSelections.length === 4) return;
      setTotalSelections((prev) => [...prev, index]);
    }
  };

  const handleNext = () => {
    if (!isStarted) {
      socket.emit("taskActivityStatus", {
        taskId: TASK_ID,
        status: { ready: true }
      });
      setIsStarted(true);
      return;
    } else {
      const nextRoute = getNextRoute("/task-9", platform);
      socket.emit("navigate", nextRoute);
    }
  };

  const handleComplete = () => {
    console.log("handleComplete");
    socket?.emit("updateTask9", { id, totalSelections });
    setFinishedTask(true);
  };

  const confirmChoices = () => {
    if (totalSelections.length < 4) {
      toast.info(t("task9.a.selectAtLeastFour") || "Please select at least 4 words.");
      return;
    }
    socket.emit("task9UserSubmit", { id, totalSelections });
  };

  const handleButtonClick = () => {
    if (!isStarted && role !== defaultRoles.participant) {
      return handleNext;
    } else if (isStarted && role !== defaultRoles.moderator && !confirmed) {
      return confirmChoices;
    } else if (role !== defaultRoles.participant && finishedTask) {
      return handleNext;
    } else if (
      role === defaultRoles.moderator ||
      (role === defaultRoles.moderatorAndParticipant && confirmed)
    ) {
      return handleNext;
    } else {
      return null;
    }
  };

  const buttonColor =
    (role === defaultRoles.moderator && isStarted) ||
    (role === defaultRoles.moderatorAndParticipant && confirmed)
      ? "green"
      : role !== defaultRoles.participant && !isStarted
        ? "green"
        : isStarted && role !== defaultRoles.moderator && !confirmed
          ? "orange"
          : role !== defaultRoles.moderator && isStarted
            ? "orange"
            : "green";

  const buttonText =
    (role === defaultRoles.moderator && isStarted) ||
    (role === defaultRoles.moderatorAndParticipant && confirmed)
      ? t("buttonNext") + " →"
      : role !== defaultRoles.participant && !isStarted
        ? t("task9.a.start") + " →"
        : isStarted && role !== defaultRoles.moderator && !confirmed
          ? t("task9.a.confirm") + " →"
          : role !== defaultRoles.moderator && isStarted
            ? t("task9.a.complete") + " →"
            : null;

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-9`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton = role !== defaultRoles.participant && !isStarted;

  return (
    <RemoteLayout
      buttonText={buttonText}
      onButtonClick={handleButtonClick()}
      buttonColor={buttonColor}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl font-bold flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {isStarted ? t("task9.a.started.title") : t("task9.a.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl mx-auto text-white-100 text-lg"
          dangerouslySetInnerHTML={{
            __html: isStarted ? t("task9.a.started.description") : t("task9.a.description")
          }}
        />
      </div>

      {/* Main Content */}
      {isStarted ? (
        <>
          <div className="flex flex-col md:flex-row md:items-start gap-4 w-full">
            <div className="bg-orange-50 text-black p-6 rounded-2xl shadow-2xl w-full md:w-4/12 mt-5">
              <h2 className="text-orange-default font-bold text-xl mb-4">
                {t("task9.a.card.title")}
              </h2>
              <ul
                className="list-inside list-disc"
                dangerouslySetInnerHTML={{ __html: t("task9.a.card.list.item1") }}
              />
            </div>

            <div className="w-full md:w-5/12 mx-auto">
              {(role === defaultRoles.moderator ||
                (role === defaultRoles.moderatorAndParticipant &&
                  readyUsers.includes(id?.toString()))) && (
                <ParticipantsReadyList
                  readyUsers={readyUsers}
                  showTimer={false}
                  readyText={t("task9.readyUser")}
                  readyBGColor={"bg-green-300"}
                  containClasses={"!bg-orange-50"}
                />
              )}
              {role !== defaultRoles.moderator && (
                <div className="flex flex-wrap gap-2 items-center justify-start content-center my-4">
                  {wordOptions.map(
                    (word, index) =>
                      (!confirmed || (confirmed && totalSelections.includes(index))) && (
                        <span
                          key={index}
                          className={`w-full md:w-fit text-center px-4 py-2 rounded-full cursor-pointer  ${
                            totalSelections.includes(index)
                              ? "bg-orange-100 text-white-100 md:hover:bg-opacity-75"
                              : "bg-white-100 border border-gray-250 text-black-100 md:hover:bg-gray-300 md:hover:text-orange-100 md:hover:border-orange-100"
                          }`}
                          dangerouslySetInnerHTML={{ __html: word }}
                          onClick={() => {
                            if (confirmed) return;
                            selectWord(index);
                          }}
                        />
                      )
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="overflow-y-auto mt-8 w-full">
            <Conference isLinear={true} />
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 w-full">
          {/* Task Overview Card */}
          <div className="bg-orange-50 text-black p-6 rounded-2xl shadow-2xl col-span-1 w-full h-fit">
            <h2 className="text-orange-default font-bold text-xl mb-4">
              {t("task9.a.card.title")}
            </h2>
            <ul
              className="list-inside"
              dangerouslySetInnerHTML={{ __html: t("task9.a.card.list.item1") }}
            />
          </div>
          <div className="flex flex-col gap-4 w-full col-span-1 md:col-span-2">
            {/* Participants Grid */}
            <Conference smallSpace={true} />
          </div>
        </div>
      )}
    </RemoteLayout>
  );
};

export default Task9;
